import { useRef } from "react";
import {
  Camera,
  Briefcase,
  FileText,
  MapPin,
  Calendar,
  Edit3,
  ChevronDown,
  Loader2,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import type { ProfileDetail } from "@/interface";
import { toast, getInitials } from "@/utils";
import { Avatar } from "@/components/ui/avatar";
import { usePatchProfile } from "./sections/hooks/useProfile";

export default function ProfileHeader({
  profileData,
  onEditClick,
}: {
  profileData: ProfileDetail;
  onEditClick: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const patchProfileMutation = usePatchProfile();

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const allowedTypes = ["image/jpeg", "image/png", "image/jpg"];
    const allowedExtensions = /\.(jpg|jpeg|png)$/i;

    if (
      !allowedTypes.includes(file.type) &&
      !allowedExtensions.test(file.name)
    ) {
      toast.error("Only JPEG, PNG, and JPG image formats are allowed.");
      e.target.value = "";
      return;
    }

    const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB
    if (file.size > maxSizeInBytes) {
      toast.error("File size must not exceed 5 MB.");
      e.target.value = "";
      return;
    }

    try {
      const formData = new FormData();
      formData.append("profile_picture", file);

      if (profileData?.id) {
        await patchProfileMutation.mutateAsync({
          id: profileData.id,
          payload: formData,
        });
        // toast.success("Profile picture updated successfully!");
      }
    } catch (err: any) {
      toast.error(err?.message || "Failed to update profile picture.");
    } finally {
      e.target.value = "";
    }
  };

  const fullName =
    (profileData?.first_name
      ? `${profileData.first_name} ${profileData.last_name ?? ""}`.trim()
      : "") ||
    profileData?.username ||
    "";

  const employeeCode = profileData?.employee_code || "";

  const status = profileData?.employment_status || "";
  const jobRole = profileData?.job_role || "";
  const department = profileData?.department_detail?.department_name || "";

  const location =
    profileData?.city || profileData?.state || profileData?.country || "N/A";

  const joiningDate = profileData?.joining_date || "N/A";

  const lastUpdated = profileData?.updated_at
    ? new Date(profileData.updated_at).toLocaleDateString()
    : "";

  const lastUpdatedBy = profileData?.updated_by_name || fullName || "";

  return (
    <div className="bg-[#110B29] rounded-2xl p-8 lg:p-10 relative overflow-hidden shadow-lg border border-purple-900/30">
      <div className="absolute top-0 right-0 w-2/3 h-full bg-gradient-to-l from-purple-900/40 to-transparent pointer-events-none"></div>

      <div className="relative z-10 flex flex-col lg:flex-row gap-6 items-start lg:items-center">
        {/* Avatar Section */}
        <div className="relative shrink-0">
          <div className="w-28 h-28 lg:w-32 lg:h-32 rounded-full border-4 border-white shadow-xl overflow-hidden bg-slate-200">
            <Avatar
              src={profileData?.profile_picture}
              name={fullName}
              className="w-full h-full"
              fallbackClassName="text-3xl lg:text-4xl"
            />
          </div>
          <input
            type="file"
            ref={fileInputRef}
            accept="image/jpeg,image/png,image/jpg"
            className="hidden"
            onChange={handleFileChange}
          />
          <Button
            size="icon"
            type="button"
            onClick={() => fileInputRef.current?.click()}
            disabled={patchProfileMutation.isPending}
            className="absolute bottom-1 right-1 w-8 h-8 bg-purple-600 hover:bg-purple-700 text-white rounded-full border-2 border-white shadow-sm disabled:opacity-70"
          >
            {patchProfileMutation.isPending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Camera className="w-4 h-4" />
            )}
          </Button>
        </div>

        {/* Info Section */}
        <div className="flex-1 text-white">
          <div className="flex items-center gap-3 mb-1">
            <h1 className="text-2xl lg:text-3xl font-bold">{fullName}</h1>
            {/* <span className="bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-xs font-bold px-2 py-0.5 rounded-full capitalize">
              {status}
            </span> */}
          </div>
          {employeeCode && (
            <p className="text-sm text-slate-400 mb-4">{employeeCode}</p>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-y-4 gap-x-6 text-sm text-white mb-6 max-w-3xl">
            <div className="flex items-center gap-2">
              <Briefcase className="w-4 h-4" />
              {jobRole}
            </div>
            <div className="flex items-center gap-2">
              <FileText className="w-4 h-4" />
              {department} Department
            </div>
            <div className="flex items-center gap-2">
              <MapPin className="w-4 h-4" />
              {location}
            </div>

            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4" />
              Joined {joiningDate}
            </div>
          </div>
        </div>

        {/* Right Actions Section */}
        <div className="lg:w-48 shrink-0 flex flex-col h-full mt-2">
          <div className="flex w-full mb-6 bg-white/5 border border-white/40 rounded-full overflow-hidden shadow-sm">
            <Button
              variant="ghost"
              onClick={onEditClick}
              className="flex-1 hover:bg-white/10 text-white text-sm font-medium h-9 flex items-center justify-center gap-2 rounded-none border-r border-white/40"
            >
              <Edit3 className="w-3.5 h-3.5" /> Edit Profile
            </Button>
            {/* <Button
              size="icon"
              variant="ghost"
              className="w-9 h-9 hover:bg-white/10 text-white rounded-none"
            >
              <ChevronDown className="w-4 h-4" />
            </Button> */}
          </div>

          <div className="text-left text-xs space-y-4 pl-1">
            <div>
              <p className="text-white mb-1">Profile Last Updated</p>
              <p className="text-white">{lastUpdated}</p>
            </div>

            <div>
              <p className="text-white mb-1">Profile Last Updated By</p>
              <p className="text-white flex items-center gap-1.5">
                {/* <span className="w-2 h-2 rounded-full bg-emerald-500 shadow-[0_0_8px_rgba(16,185,129,0.8)]"></span>{" "} */}
                {lastUpdatedBy}
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
